import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Avatar, AgGridTable, Button, Pagination, DateRangePicker } from 'components/common';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { FaSearch, FaSyncAlt, FaTimes } from 'react-icons/fa';
import { FaFileExcel } from 'react-icons/fa6';
import { MdFilterAlt, MdOutlineRequestQuote, MdPublic, MdStorefront } from 'react-icons/md';
import { LuMapPin } from 'react-icons/lu';
import { PersonRenderer, iconCell } from 'components/common/ag-grid';
import dayjs from 'dayjs';
import { getQuotations, exportQuotation } from 'redux/crm/report/reportSlice';
import { getActiveSalesUnits } from 'redux/crm/salesUnitSlice';
import AdvanceFilterDrawer, { getEmptyValues } from 'components/common/AdvanceFilterDrawer';
import { debounce } from 'helpers/debounce';

const SEARCH_FIELD = 'project_q_number_or_project_j_number_or_project_name_cont';

const THIS_YEAR_START = dayjs().startOf('year').format('YYYY-MM-DD');
const THIS_YEAR_END   = dayjs().format('YYYY-MM-DD');

const fmtDate = (v) => v ? dayjs(v).format('DD-MMM-YYYY') : '';

// Copy of `obj` without `keys` (filter params replaced by a newer selection).
const omitKeys = (obj, keys) =>
  Object.fromEntries(Object.entries(obj || {}).filter(([k]) => !keys.includes(k)));

// ── Compact multi-select dropdown for Sales Unit ──────────────────────────────
const SalesUnitPicker = ({ options, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [dropPos, setDropPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      const outsideTrigger = triggerRef.current && !triggerRef.current.contains(e.target);
      const outsideDropdown = !dropdownRef.current || !dropdownRef.current.contains(e.target);
      if (outsideTrigger && outsideDropdown) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleToggle = () => {
    if (!open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropPos({ top: rect.bottom + 6, left: rect.left });
    }
    setOpen((o) => !o);
  };

  const isSelected = (opt) => value.some((v) => v.value === opt.value);
  const toggle = (opt) => {
    const next = isSelected(opt)
      ? value.filter((v) => v.value !== opt.value)
      : [...value, opt];
    onChange(next);
  };
  const clear = (e) => { e.stopPropagation(); onChange([]); };

  const display = value.length === 0 ? 'All Units'
    : value.length === 1 ? value[0].label
    : `${value.length} units`;

  const hasValue = value.length > 0;

  const groups = useMemo(() => {
    const map = {};
    options.forEach((opt) => {
      const key = opt.region || 'Other';
      if (!map[key]) map[key] = [];
      map[key].push(opt);
    });
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  }, [options]);

  return (
    <div ref={triggerRef} className="tw-relative tw-inline-block">
      <button
        type="button"
        onClick={handleToggle}
        className={`tw-inline-flex tw-items-center tw-gap-2 tw-h-8 tw-px-3 tw-rounded tw-border tw-text-xs tw-font-medium tw-cursor-pointer tw-transition-colors tw-whitespace-nowrap ${
          hasValue
            ? 'tw-bg-blue-50 tw-border-blue-400 tw-text-blue-700'
            : 'tw-bg-white tw-border-slate-300 tw-text-slate-600 hover:tw-border-blue-400 hover:tw-text-blue-600'
        }`}
      >
        <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
          <path d="M1.5 1a.5.5 0 0 0-.5.5v2a.5.5 0 0 0 1 0V2h12v1.5a.5.5 0 0 0 1 0v-2a.5.5 0 0 0-.5-.5h-13z"/>
          <path d="M0 8a.5.5 0 0 1 .5-.5h15a.5.5 0 0 1 0 1H.5A.5.5 0 0 1 0 8zm0 4a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7A.5.5 0 0 1 0 12z"/>
        </svg>
        {display}
        {hasValue && (
          <span
            onClick={clear}
            className="tw-ml-0.5 tw-rounded-xl tw-w-3.5 tw-h-3.5 tw-flex tw-items-center tw-justify-center tw-bg-blue-200 hover:tw-bg-blue-300 tw-text-blue-700 tw-cursor-pointer tw-text-xs tw-leading-none"
          >
            ×
          </span>
        )}
      </button>

      {open && ReactDOM.createPortal(
        <>
          <div className="tw-fixed tw-inset-0 tw-z-[9998]" onClick={() => setOpen(false)} />
          <div
            ref={dropdownRef}
            className="tw-fixed tw-z-[9999] tw-bg-white tw-rounded-lg tw-shadow-2xl tw-border tw-border-slate-200 tw-py-1.5"
            style={{ top: dropPos.top, left: dropPos.left, minWidth: 220, maxHeight: 360, overflowY: 'auto' }}
          >
            {options.length === 0 && (
              <p className="tw-text-xs tw-text-slate-400 tw-px-3 tw-py-2">No options</p>
            )}
            {groups.map(([region, opts]) => (
              <div key={region}>
                <div className="tw-px-3 tw-pt-2 tw-pb-0.5 tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-slate-400 tw-bg-slate-50 tw-border-b tw-border-slate-100 tw-sticky tw-top-0">
                  {region}
                </div>
                {opts.map((opt) => (
                  <label
                    key={opt.value}
                    className="tw-flex tw-items-center tw-gap-2.5 tw-px-3 tw-py-1.5 hover:tw-bg-slate-50 tw-cursor-pointer tw-text-xs tw-text-slate-700 tw-font-medium tw-select-none"
                  >
                    <input
                      type="checkbox"
                      checked={isSelected(opt)}
                      onChange={() => toggle(opt)}
                      className="tw-w-3.5 tw-h-3.5 tw-accent-primary-600 tw-cursor-pointer"
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            ))}
          </div>
        </>,
        document.body
      )}
    </div>
  );
};

const salesUnitOptionShape = PropTypes.shape({
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.string,
  region: PropTypes.string
});

SalesUnitPicker.propTypes = {
  options: PropTypes.arrayOf(salesUnitOptionShape).isRequired,
  value: PropTypes.arrayOf(salesUnitOptionShape).isRequired,
  onChange: PropTypes.func.isRequired
};

// ── Column definitions (nested paths match the serializer output) ─────────────
const COLUMN_DEFS = [
  {
    headerName: ' ',
    children: [
      { headerName: 'PCV #',          field: 'project.pcv_number',     minWidth: 110 },
      {
        headerName: 'Quote #',        field: 'project.q_number',       minWidth: 130,
        cellRenderer: ({ data }) => data?.project?.q_number
          ? <Link to={`/crm/projects/${data.project.id}`} className="tw-text-blue-600">{data.project.q_number}</Link>
          : null,
      },
      {
        headerName: 'Project Name',   field: 'project.name',           minWidth: 200,
        cellRenderer: ({ data }) => data?.project?.name
          ? <Link to={`/crm/projects/${data.project.id}`} className="tw-text-blue-600">{data.project.name}</Link>
          : null,
      },
      {
        headerName: 'P/J Number',    field: 'j_number',               minWidth: 130,
        cellRenderer: ({ value, data }) => {
          if (!value) return null;
          if (!data?.project?.id) return value;
          return <Link to={`/crm/projects/${data.project.id}`} className="tw-text-blue-600">{value}</Link>;
        },
      },
      { headerName: 'Building',       field: 'name',                    minWidth: 100 },
      { headerName: 'Location',       field: 'project.pj_location',    minWidth: 150, cellRenderer: iconCell(LuMapPin) },
      {
        headerName: 'Customer',       field: 'project.customer_name',  minWidth: 200,
        cellRenderer: ({ data }) => data?.project?.customer_name
          ? (
            <span className="tw-inline-flex tw-items-center tw-gap-2 tw-max-w-full">
              <Avatar name={data.project.customer_name} alt={data.project.customer_name} size="xs" className="tw-shrink-0" />
              <Link to={`/crm/customers/${data.project.customer_id}`} className="tw-text-blue-600 tw-truncate">{data.project.customer_name}</Link>
            </span>
          )
          : null,
      },
      { headerName: 'Status',         field: 'status.name',            minWidth: 130 },
      { headerName: 'Sales Incharge', field: 'project.sales_incharge', minWidth: 120, cellRenderer: PersonRenderer },
      { headerName: 'Sales Unit',     field: 'sales_unit.description', minWidth: 130, cellRenderer: iconCell(MdStorefront) },
      { headerName: 'Region',         field: 'sales_unit.region',      minWidth: 100, cellRenderer: iconCell(MdPublic) },
      { headerName: 'Entry Date',     field: 'quotation_entry_date',   minWidth: 120, cellRenderer: ({ value }) => fmtDate(value) },
      { headerName: 'Rel. Wt (M.T)',  field: 'est_released_weight',   minWidth: 130 },
      { headerName: 'Final Wt (M.T)', field: 'est_final_weight',      minWidth: 130 },
    ],
  },
  {
    headerName: 'Latest Revision',
    children: [
      {
        headerName: 'REV',         field: 'lastest_revision.rev',          minWidth: 70,
        cellRenderer: ({ value }) => value ? <span className="tw-text-blue-600 tw-font-semibold">{value}</span> : null,
      },
      { headerName: 'Req. By',    field: 'lastest_revision.requested_by', minWidth: 100, cellRenderer: PersonRenderer },
      { headerName: 'Requested',  field: 'lastest_revision.requested',    minWidth: 120, cellRenderer: ({ value }) => fmtDate(value) },
      { headerName: 'Scheduled',  field: 'lastest_revision.scheduled',   minWidth: 120, cellRenderer: ({ value }) => fmtDate(value) },
      { headerName: 'Released',   field: 'lastest_revision.released',    minWidth: 120, cellRenderer: ({ value }) => fmtDate(value) },
      { headerName: 'Designer',   field: 'lastest_revision.designer',    minWidth: 90, cellRenderer: PersonRenderer },
      { headerName: 'Checker',    field: 'lastest_revision.checker',     minWidth: 90, cellRenderer: PersonRenderer },
      { headerName: 'Note',       field: 'lastest_revision.note',        minWidth: 180 },
    ],
  },
  {
    headerName: '',
    children: [
      { headerName: 'Building note', field: 'sts_notes', minWidth: 120 },
    ],
  },
];

// Drawer-only filters (inline filters handled separately)
const BASE_FILTER_FIELDS = [
  { key: 'q_number',       label: 'Quote Number',  type: 'text',     paramKey: 'project_q_number_cont',                       tagName: 'Quote #' },
  { key: 'project_name',   label: 'Project Name',  type: 'text',     paramKey: 'project_name_cont',                           tagName: 'Project' },
  { key: 'is_scheduled',   label: 'Scheduled',     type: 'checkbox', paramKey: 'lastest_revision_est_scheduled_date_not_null', tagName: 'Scheduled' },
  { key: 'is_released',    label: 'Released',      type: 'checkbox', paramKey: 'lastest_revision_est_released_date_not_null',  tagName: 'Released' },
  { key: 'sales_incharge', label: 'Sales Incharge',type: 'text',     paramKey: 'project_sales_incharge_cont',                 tagName: 'Sales Incharge' },
];

// ── Inline filter label helper ────────────────────────────────────────────────
const InlineLabel = ({ children }) => (
  <span className="tw-text-xs tw-text-slate-500 tw-font-medium tw-flex-shrink-0">{children}</span>
);
InlineLabel.propTypes = { children: PropTypes.node };

const Divider = () => <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" />;

// ── Page component ────────────────────────────────────────────────────────────
const QuotationPage = () => {
  const dispatch = useDispatch();
  const {
    quotationData: { data = [], totalRows = 0, totalPagesCount = 1, exporting = false },
  } = useSelector((s) => s.crm_report);
  const { activeSalesUnitsData = [] } = useSelector((s) => s.crm_sales_unit);

  const salesUnitOptions = useMemo(
    () => activeSalesUnitsData.map((u) => ({ value: u.id, label: u.description, region: u.region })),
    [activeSalesUnitsData]
  );

  const [page, setPage]               = useState(1);
  const [perPage, setPerPage]         = useState(50);
  const [sort, setSort]               = useState({ attribute: 'id', direction: 'desc' });
  const [search, setSearch]           = useState('');
  const [entryDate, setEntryDate]     = useState({ startDate: THIS_YEAR_START, endDate: THIS_YEAR_END });
  const [scheduleDate, setScheduleDate] = useState({ startDate: '', endDate: '' });
  const [releaseDate, setReleaseDate]   = useState({ startDate: '', endDate: '' });
  const [salesUnits, setSalesUnits]     = useState([]);
  const [filterValues, setFilterValues] = useState(() => getEmptyValues(BASE_FILTER_FIELDS));
  const [filterTags, setFilterTags]     = useState([]);
  const [showFilter, setShowFilter]     = useState(false);
  const [loading, setLoading]           = useState(false);

  const activeFilterParamsRef = useRef({
    quotation_entry_date_gteq: THIS_YEAR_START,
    quotation_entry_date_lteq: THIS_YEAR_END,
  });
  const searchRef = useRef('');

  const totalPages = totalPagesCount || Math.ceil(totalRows / perPage) || 1;

  useEffect(() => { dispatch(getActiveSalesUnits()); }, [dispatch]);

  const buildParams = useCallback(
    (overrides = {}) => ({
      page, per_page: perPage, sort,
      ...(searchRef.current ? { [SEARCH_FIELD]: searchRef.current } : {}),
      ...activeFilterParamsRef.current,
      ...overrides,
    }),
    [page, perPage, sort]
  );

  const fetchData = useCallback(async (params) => {
    setLoading(true);
    try { await dispatch(getQuotations(params)); } finally { setLoading(false); }
  }, [dispatch]);

  useEffect(() => { fetchData(buildParams()); }, [buildParams, fetchData]);

  const debouncedFetch = useRef(debounce((params) => fetchData(params), 500)).current;

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleSearchChange = (e) => {
    const val = e.target.value;
    searchRef.current = val;
    setSearch(val);
    debouncedFetch({ page: 1, per_page: perPage, sort, ...(val ? { [SEARCH_FIELD]: val } : {}), ...activeFilterParamsRef.current });
  };

  const handleClearSearch = () => {
    searchRef.current = '';
    setSearch('');
    fetchData({ page: 1, per_page: perPage, sort, ...activeFilterParamsRef.current });
  };

  const makeDateHandler = (setStateFn, gteqKey, lteqKey) => ({ startDate, endDate }) => {
    setStateFn({ startDate, endDate });
    const rest = omitKeys(activeFilterParamsRef.current, [gteqKey, lteqKey]);
    const params = startDate && endDate ? { [gteqKey]: startDate, [lteqKey]: endDate } : {};
    activeFilterParamsRef.current = { ...rest, ...params };
    setPage(1);
    fetchData({ page: 1, per_page: perPage, sort, ...(searchRef.current ? { [SEARCH_FIELD]: searchRef.current } : {}), ...activeFilterParamsRef.current });
  };

  const handleEntryDateChange   = makeDateHandler(setEntryDate,   'quotation_entry_date_gteq',                   'quotation_entry_date_lteq');
  const handleScheduleDateChange = makeDateHandler(setScheduleDate,'lastest_revision_est_scheduled_date_gteq',   'lastest_revision_est_scheduled_date_lteq');
  const handleReleaseDateChange  = makeDateHandler(setReleaseDate, 'lastest_revision_est_released_date_gteq',    'lastest_revision_est_released_date_lteq');

  const handleSalesUnitChange = (selected) => {
    setSalesUnits(selected || []);
    const rest = omitKeys(activeFilterParamsRef.current, ['sales_unit_id_in']);
    const params = selected?.length > 0 ? { sales_unit_id_in: selected.map((s) => s.value) } : {};
    activeFilterParamsRef.current = { ...rest, ...params };
    setPage(1);
    fetchData({ page: 1, per_page: perPage, sort, ...(searchRef.current ? { [SEARCH_FIELD]: searchRef.current } : {}), ...activeFilterParamsRef.current });
  };

  const handleSort = useCallback(({ colId, sort: dir }) => {
    const newSort = dir ? { attribute: colId, direction: dir } : { attribute: 'id', direction: 'desc' };
    setSort(newSort);
    setPage(1);
    fetchData({ page: 1, per_page: perPage, sort: newSort, ...(searchRef.current ? { [SEARCH_FIELD]: searchRef.current } : {}), ...activeFilterParamsRef.current });
  }, [fetchData, perPage]);

  const handleApplyFilter = (params, tags) => {
    // Inline-controlled params are preserved and never overridden by drawer
    const inlineParams = {
      ...(entryDate.startDate && entryDate.endDate ? { quotation_entry_date_gteq: entryDate.startDate, quotation_entry_date_lteq: entryDate.endDate } : {}),
      ...(scheduleDate.startDate && scheduleDate.endDate ? { lastest_revision_est_scheduled_date_gteq: scheduleDate.startDate, lastest_revision_est_scheduled_date_lteq: scheduleDate.endDate } : {}),
      ...(releaseDate.startDate && releaseDate.endDate ? { lastest_revision_est_released_date_gteq: releaseDate.startDate, lastest_revision_est_released_date_lteq: releaseDate.endDate } : {}),
      ...(salesUnits.length > 0 ? { sales_unit_id_in: salesUnits.map((s) => s.value) } : {}),
    };
    activeFilterParamsRef.current = { ...inlineParams, ...params };
    setFilterTags(tags);
    setPage(1);
    setShowFilter(false);
    fetchData({ page: 1, per_page: perPage, sort, ...(searchRef.current ? { [SEARCH_FIELD]: searchRef.current } : {}), ...activeFilterParamsRef.current });
  };

  const handleResetFilter = () => {
    activeFilterParamsRef.current = {
      quotation_entry_date_gteq: THIS_YEAR_START,
      quotation_entry_date_lteq: THIS_YEAR_END,
    };
    setEntryDate({ startDate: THIS_YEAR_START, endDate: THIS_YEAR_END });
    setScheduleDate({ startDate: '', endDate: '' });
    setReleaseDate({ startDate: '', endDate: '' });
    setSalesUnits([]);
    setFilterValues(getEmptyValues(BASE_FILTER_FIELDS));
    setFilterTags([]);
    setPage(1);
    fetchData({ page: 1, per_page: perPage, sort, ...(searchRef.current ? { [SEARCH_FIELD]: searchRef.current } : {}), ...activeFilterParamsRef.current });
  };

  const removeTag = (tag) => {
    const newParams = Object.fromEntries(
      Object.entries(activeFilterParamsRef.current).filter(([k]) => k !== tag.id)
    );
    activeFilterParamsRef.current = newParams;
    setFilterTags((prev) => prev.filter((t) => t.id !== tag.id));
    setPage(1);
    fetchData({ page: 1, per_page: perPage, sort, ...(searchRef.current ? { [SEARCH_FIELD]: searchRef.current } : {}), ...newParams });
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="tw-flex tw-flex-col tw-gap-3 tw-p-4">

      {/* ── Header ── */}
      <div className="tw-mb-1">
        <div className="tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-3">
          <div className="tw-flex tw-items-center tw-gap-3">
            <div className="tw-w-1.5 tw-h-10 tw-rounded-sm tw-flex-shrink-0" style={{ background: 'linear-gradient(to bottom, #0ea5e9, #6366f1)' }} />
            <div className="tw-flex tw-items-center tw-gap-2">
              <MdOutlineRequestQuote className="tw-text-blue-600" size={22} />
              <div>
                <h1 className="tw-mb-0 tw-font-bold tw-text-2xl tw-text-gray-900">Quotation</h1>
                <p className="tw-text-gray-500 tw-text-sm tw-mb-0">Track engineering quotation submissions and estimation status.</p>
              </div>
            </div>
          </div>

          <div className="tw-flex tw-items-center tw-gap-2">
            <Button variant="secondary" size="md" leftIcon={FaSyncAlt} loading={loading} onClick={() => fetchData(buildParams())}>
              Reload
            </Button>
            <Button variant="secondary" size="md" leftIcon={FaFileExcel} disabled={exporting || loading} onClick={() => dispatch(exportQuotation(buildParams()))}
              className="tw-text-green-700 tw-border-green-300 tw-bg-green-50 hover:tw-bg-green-100">
              {exporting ? 'Exporting...' : 'Export'}
            </Button>
          </div>
        </div>
      </div>

      {/* ── Filter bar ── */}
      <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-slate-200 tw-overflow-visible">
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-px-3 tw-py-2.5">

          {/* Text search */}
          <div className="tw-relative tw-flex tw-items-center tw-w-full sm:tw-w-44 sm:tw-flex-shrink-0">
            <FaSearch className="tw-absolute tw-left-3 tw-text-slate-400 tw-pointer-events-none" size={12} />
            <input
              type="text"
              value={search}
              onChange={handleSearchChange}
              placeholder="Q#, J#, project name..."
              className="tw-w-full tw-pl-8 tw-pr-7 tw-py-1.5 tw-text-xs tw-border tw-border-slate-200 tw-rounded focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary-400 focus:tw-border-transparent tw-bg-slate-50 hover:tw-bg-white tw-transition-colors tw-text-slate-700 tw-placeholder-slate-400"
            />
            {search && (
              <button type="button" onClick={handleClearSearch}
                className="tw-absolute tw-right-2 tw-flex tw-items-center tw-justify-center tw-w-4 tw-h-4 tw-rounded-xl tw-bg-slate-300 hover:tw-bg-slate-400 tw-text-white tw-border-0 tw-cursor-pointer tw-transition-colors tw-p-0">
                <FaTimes size={8} />
              </button>
            )}
          </div>

          <Divider />

          {/* Entry Date — defaults to This Year */}
          <InlineLabel>Entry Date</InlineLabel>
          <DateRangePicker
            label="This Year"
            startDate={entryDate.startDate}
            endDate={entryDate.endDate}
            onChange={handleEntryDateChange}
          />

          <Divider />

          {/* Schedule Date */}
          <InlineLabel>Schedule</InlineLabel>
          <DateRangePicker
            label="All dates"
            startDate={scheduleDate.startDate}
            endDate={scheduleDate.endDate}
            onChange={handleScheduleDateChange}
          />

          <Divider />

          {/* Release Date */}
          <InlineLabel>Release</InlineLabel>
          <DateRangePicker
            label="All dates"
            startDate={releaseDate.startDate}
            endDate={releaseDate.endDate}
            onChange={handleReleaseDateChange}
          />

          <Divider />

          {/* Sales Unit */}
          <InlineLabel>Sales Unit</InlineLabel>
          <SalesUnitPicker
            options={salesUnitOptions}
            value={salesUnits}
            onChange={handleSalesUnitChange}
          />

          <Divider />

          {/* More Filters (drawer) */}
          <button
            type="button"
            onClick={() => setShowFilter(true)}
            className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded tw-border tw-text-xs tw-font-medium tw-cursor-pointer tw-transition-all tw-flex-shrink-0 ${
              filterTags.length > 0
                ? 'tw-bg-primary-600 tw-border-primary-600 tw-text-white hover:tw-bg-primary-700'
                : 'tw-bg-white tw-border-slate-200 tw-text-slate-600 hover:tw-border-primary-300 hover:tw-text-primary-600 hover:tw-bg-primary-50'
            }`}
          >
            <MdFilterAlt size={14} />
            More Filters
            {filterTags.length > 0 && (
              <span className="tw-inline-flex tw-items-center tw-justify-center tw-min-w-[18px] tw-h-[18px] tw-px-1 tw-rounded-xl tw-bg-white/30 tw-text-white tw-text-xs tw-font-bold tw-leading-none">
                {filterTags.length}
              </span>
            )}
          </button>

          {!loading && totalRows > 0 && (
            <span className="tw-ml-auto tw-flex-shrink-0 tw-text-xs tw-text-slate-400 tw-font-medium">
              {totalRows.toLocaleString()} records
            </span>
          )}
        </div>

        {/* Active drawer filter tags */}
        {filterTags.length > 0 && (
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-border-t tw-border-slate-100 tw-bg-primary-50/40">
            <span className="tw-text-xs tw-text-slate-400 tw-font-medium tw-flex-shrink-0">Active filters:</span>
            {filterTags.map((tag) => (
              <span key={tag.id} className="tw-inline-flex tw-items-center tw-gap-1 tw-pl-2.5 tw-pr-1 tw-py-0.5 tw-bg-white tw-border tw-border-primary-200 tw-text-slate-700 tw-rounded-md tw-text-xs tw-shadow-sm">
                <span className="tw-text-primary-500 tw-font-semibold">{tag.name}</span>
                <span className="tw-text-slate-300 tw-mx-0.5">·</span>
                <span className="tw-max-w-[140px] tw-truncate tw-text-slate-600">
                  {Array.isArray(tag.value) ? tag.value.join(' – ') : tag.value}
                </span>
                <button type="button" onClick={() => removeTag(tag)}
                  className="tw-ml-0.5 tw-flex tw-items-center tw-justify-center tw-w-4 tw-h-4 tw-rounded-sm tw-text-slate-400 hover:tw-bg-red-100 hover:tw-text-red-500 tw-bg-transparent tw-border-0 tw-cursor-pointer tw-transition-colors">
                  <FaTimes size={8} />
                </button>
              </span>
            ))}
            <Button variant="secondary" size="xs" leftIcon={FaTimes} onClick={handleResetFilter}
              className="tw-text-red-500 tw-border-red-200 tw-bg-transparent hover:tw-bg-red-500 hover:tw-text-white hover:tw-border-red-500">
              Clear all
            </Button>
          </div>
        )}
      </div>

      {/* ── Grid ── */}
      <div className="tw-bg-white tw-rounded tw-shadow-sm tw-border tw-border-slate-200 tw-overflow-hidden">
        <AgGridTable
          rowData={data}
          columnDefs={COLUMN_DEFS}
          loading={loading}
          rowNumbers
          pagination={false}
          filter={false}
          height="max(320px, calc(100dvh - 310px))"
          onSortChanged={handleSort}
        />
      </div>

      {/* ── Pagination ── */}
      <Pagination
        currentPage={page}
        totalPages={totalPages}
        totalResults={totalRows}
        perPage={perPage}
        pageSizeOptions={[20, 50, 100, 200]}
        onPageChange={setPage}
        onPageSizeChange={(s) => { setPerPage(s); setPage(1); }}
        disabled={loading}
      />

      {/* ── Advanced filter drawer (Quote #, Project Name, Scheduled, Released, Sales Incharge) ── */}
      <AdvanceFilterDrawer
        show={showFilter}
        onClose={() => setShowFilter(false)}
        onApply={handleApplyFilter}
        onReset={handleResetFilter}
        fields={BASE_FILTER_FIELDS}
        values={filterValues}
        onChange={(k, v) => setFilterValues((prev) => ({ ...prev, [k]: v }))}
        title="More Filters"
        dividerAfter={['is_released']}
      />
    </div>
  );
};

export default QuotationPage;
